'use client';

import React, { useId, useState } from 'react';
import { ArrowUpRight, CornerDownLeft } from 'lucide-react';
import { CtaButton, Eyebrow, Heading, Text } from '@/components/ds';
import { useSignupModeState } from '@/components/utils/SignupModeProvider';
import { cn } from '@/lib/twUtils';
import { useAskAction, type AskPosition } from './useNewsVisitor';

interface NewsAskBoxProps {
  slug: string;
  /** I quesiti pronti dell'articolo (`suggested_questions`) */
  questions: string[];
  position: AskPosition;
  /**
   * `panel`: il riquadro largo dentro e in fondo all'articolo, col campo da
   * scrivere. `compact`: la colonna laterale, dove ogni quesito è già un link.
   */
  variant?: 'panel' | 'compact';
  title?: string;
  text?: string;
  /**
   * Solo `compact`: senza il bottone in fondo. Per chi non ha un account la CTA
   * sta nel dock in fondo allo schermo, e qui gli finirebbe dietro.
   */
  withoutMainCta?: boolean;
  className?: string;
}

/**
 * «Chiedilo a Trovanorma»: il motivo per cui la sezione esiste.
 *
 * Chi legge un articolo su una sentenza ha quasi sempre un cliente in mente, e
 * il suo caso non è mai identico a quello deciso. Il riquadro gli mette davanti
 * la domanda già scritta — quella che farebbe lui — e un solo gesto per avere
 * la risposta con le fonti. Cosa succede al clic lo decide `useAskAction`: chi
 * ha un account va al quesito precompilato nell'app, gli altri alla CTA del
 * sito (prova o demo, secondo il backend).
 */
const NewsAskBox = ({
  slug,
  questions,
  position,
  variant = 'panel',
  title,
  text,
  withoutMainCta = false,
  className,
}: NewsAskBoxProps) => {
  const fieldId = useId();
  const action = useAskAction(slug, position);
  const { isSettled } = useSignupModeState();
  const [draft, setDraft] = useState(questions[0] ?? '');
  const [kind, setKind] = useState<'suggested' | 'custom'>('suggested');

  // Finché la modalità non è nota il bottone c'è ma non si vede, come le CTA
  // del sito: nessuno legge un'etichetta che mezzo secondo dopo cambia.
  const pending = !isSettled;

  if (variant === 'compact') {
    const main = action(null);
    return (
      <div className={cn('rounded-ds-lg border border-edge bg-white p-5', className)}>
        <Eyebrow>Chiedilo a Trovanorma</Eyebrow>
        <Heading as="p" size="xs" className="mt-2.5">
          {title ?? 'E nel caso del tuo cliente?'}
        </Heading>
        {/* Sono esempi, e va detto: chi legge deve capire che può chiedere altro */}
        <Text size="note" tone="subtle" className="mt-3">
          Per esempio:
        </Text>
        <ul className="mt-2 flex flex-col gap-2">
          {questions.slice(0, 3).map(question => {
            const ask = action(question, 'suggested');
            return (
              <li key={question}>
                <a
                  href={ask.href}
                  onClick={ask.onClick}
                  className="group flex items-start gap-2 rounded-ds-sm border border-edge-soft bg-mist px-3 py-2.5 text-[0.84375rem] leading-[1.45] text-ink-strong transition-colors ds-focus hover:border-edge-strong hover:bg-white"
                >
                  <span className="min-w-0 flex-1">{question}</span>
                  <ArrowUpRight
                    aria-hidden="true"
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-ghost transition-colors group-hover:text-accent"
                  />
                </a>
              </li>
            );
          })}
        </ul>
        {!withoutMainCta && (
          <div
            data-cta-pending={pending || undefined}
            className={cn('mt-4 transition-opacity duration-200', pending && 'invisible opacity-0')}
          >
            {/* In outline: l'arancione in pagina è del riquadro largo, e questa
                colonna gli sta accanto per tutta la lettura */}
            <CtaButton href={main.href} onClick={main.onClick} variant="outline" size="sm" block>
              {main.label}
            </CtaButton>
            {main.note && (
              <Text size="note" tone="subtle" className="mt-2.5 text-center">
                {main.note}
              </Text>
            )}
          </div>
        )}
      </div>
    );
  }

  const trimmed = draft.trim();
  const ask = action(trimmed || null, trimmed ? kind : 'generic');

  return (
    <aside
      aria-labelledby={`${fieldId}-title`}
      className={cn(
        'print:hidden rounded-ds-xl border border-edge bg-[linear-gradient(150deg,#ffffff,var(--color-mist-deep))] p-4 sm:p-[clamp(1.375rem,3.2vw,2.25rem)]',
        className
      )}
    >
      <Eyebrow>Chiedilo a Trovanorma</Eyebrow>
      <Heading as="h2" size="sm" id={`${fieldId}-title`} className="mt-3 max-w-[26ch]">
        {title ?? 'Il caso del tuo cliente ha sempre un dettaglio in più'}
      </Heading>
      <Text size="body" className="mt-3 max-w-[60ch]">
        {text ??
          'Scrivi la domanda come la faresti a un collega. La risposta arriva con norme, prassi e giurisprudenza citate, e ogni fonte si apre, come in questa pagina.'}
      </Text>

      <div className="mt-5 rounded-ds-lg border border-edge bg-white p-3 shadow-[0_10px_34px_rgb(0_25_55/0.07)] focus-within:border-edge-strong">
        <label htmlFor={fieldId} className="sr-only">
          Il tuo quesito
        </label>
        <textarea
          id={fieldId}
          rows={3}
          value={draft}
          onChange={event => {
            setDraft(event.target.value);
            setKind('custom');
          }}
          placeholder="Descrivi il caso del tuo cliente: i fatti, le date, a che punto è la pratica…"
          className="block w-full resize-none bg-transparent px-2 py-1.5 text-[0.96875rem] leading-[1.55] text-ink outline-none placeholder:text-ink-ghost"
        />
        <div className="mt-2 flex flex-col gap-3 border-t border-edge-soft pt-3 sm:flex-row sm:items-center sm:justify-between">
          <Text size="note" tone="subtle" className="px-2">
            Fonti citate e verificabili, direttamente nella risposta.
          </Text>
          <div
            data-cta-pending={pending || undefined}
            className={cn(
              'shrink-0 transition-opacity duration-200',
              pending && 'invisible opacity-0'
            )}
          >
            <CtaButton href={ask.href} onClick={ask.onClick} size="sm" className="w-full sm:w-auto">
              {ask.label}
              <CornerDownLeft aria-hidden="true" className="h-4 w-4" />
            </CtaButton>
          </div>
        </div>
      </div>

      {questions.length > 1 && (
        <div className="mt-4">
          <Text size="note" tone="subtle">
            Oppure parti da un esempio:
          </Text>
          <ul className="mt-2 flex flex-col gap-1.5">
            {questions.map(question => (
              <li key={question}>
                <button
                  type="button"
                  onClick={() => {
                    setDraft(question);
                    setKind('suggested');
                  }}
                  aria-pressed={draft === question}
                  className={cn(
                    'w-full rounded-ds-sm border px-3.5 py-2.5 text-left text-[0.875rem] leading-[1.5] transition-colors ds-focus',
                    draft === question
                      ? 'border-edge-strong bg-white text-primary'
                      : 'border-edge-soft bg-white/60 text-ink-strong hover:border-edge-strong hover:bg-white'
                  )}
                >
                  {question}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {ask.note && (
        <Text
          size="note"
          tone="subtle"
          data-cta-pending={pending || undefined}
          className={cn('mt-4 transition-opacity duration-200', pending && 'invisible opacity-0')}
        >
          {ask.note}
        </Text>
      )}
    </aside>
  );
};

export default NewsAskBox;
